import { useCallback, useEffect, useState } from 'react';
import { ShoppingCart, Wallet, Truck, Undo2, TrendingUp } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { StatCard } from '@/components/ui/StatCard';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

type Preset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

interface StatusCount {
  status: string;
  count: number;
}

interface ReturnReason {
  reason: string;
  count: number;
}

interface Summary {
  orders_total: number;
  orders_value_bdt: number;
  cod_orders_count: number;
  cod_orders_value_bdt: number;
  prepaid_orders_count: number;
  prepaid_collected_bdt: number;
  cancelled_count: number;
  payment_collected_bdt: number;
  cod_collected_bdt: number;
  cod_pending_bdt: number;
  status_breakdown: StatusCount[];
  returns_count: number;
  return_reasons: ReturnReason[];
}

const STATUS_TONE: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  placed: 'primary',
  confirmed: 'primary',
  shipped: 'warning',
  delivered: 'success',
  cancelled: 'error',
  returned: 'error',
};

// core_get_daily_summary interprets dates as Asia/Dhaka calendar days (the
// business's actual "today"), so the range picker must compute in the same
// timezone — a staff member's browser clock/locale shouldn't shift which
// orders count as "today's".
function dhakaDateStr(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

function presetRange(preset: Preset): { from: string; to: string } {
  const now = new Date();
  const today = dhakaDateStr(now);
  if (preset === 'yesterday') {
    const y = dhakaDateStr(new Date(now.getTime() - 24 * 60 * 60 * 1000));
    return { from: y, to: y };
  }
  if (preset === 'week') {
    return { from: dhakaDateStr(new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)), to: today };
  }
  if (preset === 'month') {
    const [y, m] = today.split('-');
    return { from: `${y}-${m}-01`, to: today };
  }
  return { from: today, to: today };
}

export default function RxkDailySummary() {
  const [preset, setPreset] = useState<Preset>('today');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (preset === 'custom') return;
    const { from, to } = presetRange(preset);
    setDateFrom(from);
    setDateTo(to);
  }, [preset]);

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    const { data, error } = await supabase.rpc('core_get_daily_summary', { p_date_from: dateFrom, p_date_to: dateTo });
    setLoading(false);
    if (error) {
      console.error('[daily-summary] load failed:', error.message);
      setSummary(null);
      return;
    }
    setSummary(data as Summary);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const rangeLabel = dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`;

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div>
          <h1 className="text-[22px] font-bold text-ink tracking-tight">Daily Summary</h1>
          <p className="text-[12.5px] text-ink-faint mt-0.5">Orders, payments and returns for {rangeLabel || '…'}</p>
        </div>

        <Card>
          <CardBody className="flex items-end gap-3 flex-wrap">
            <Field label="Range">
              <Select value={preset} onChange={(e) => setPreset(e.target.value as Preset)} className="!w-40">
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">Last 7 Days</option>
                <option value="month">This Month</option>
                <option value="custom">Custom</option>
              </Select>
            </Field>
            {preset === 'custom' && (
              <>
                <Field label="From">
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="!w-40" />
                </Field>
                <Field label="To">
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="!w-40" />
                </Field>
              </>
            )}
            <Button variant="secondary" onClick={load} loading={loading}>
              Refresh
            </Button>
          </CardBody>
        </Card>

        {loading && !summary ? (
          <Spinner />
        ) : !summary ? (
          <EmptyState title="Couldn't load the summary" hint="Try refreshing." />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total Orders"
                value={summary.orders_total}
                icon={<ShoppingCart className="w-4 h-4" />}
                accent="primary"
                footer={`৳${summary.orders_value_bdt.toLocaleString()} total value`}
              />
              <StatCard
                label="Payment Collected"
                value={`৳${summary.payment_collected_bdt.toLocaleString()}`}
                icon={<Wallet className="w-4 h-4" />}
                accent="success"
                footer={`COD ৳${summary.cod_collected_bdt.toLocaleString()} + Prepaid ৳${summary.prepaid_collected_bdt.toLocaleString()}`}
              />
              <StatCard
                label="COD Orders"
                value={summary.cod_orders_count}
                icon={<Truck className="w-4 h-4" />}
                accent="warning"
                footer={`৳${summary.cod_pending_bdt.toLocaleString()} still pending`}
              />
              <StatCard
                label="Returns"
                value={summary.returns_count}
                icon={<Undo2 className="w-4 h-4" />}
                accent="error"
                footer={`${summary.cancelled_count} order(s) cancelled`}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Card hoverable>
                <CardHeader title="Order Status Breakdown" icon={<TrendingUp className="w-4 h-4" />} />
                <CardBody className="space-y-1">
                  {summary.status_breakdown.length === 0 ? (
                    <EmptyState title="No orders in this range" />
                  ) : (
                    summary.status_breakdown.map((s) => (
                      <div key={s.status} className="flex items-center justify-between px-2 py-2">
                        <Badge tone={STATUS_TONE[s.status] ?? 'neutral'}>{s.status}</Badge>
                        <p className="text-[13px] font-bold text-ink tabular-nums">{s.count}</p>
                      </div>
                    ))
                  )}
                </CardBody>
              </Card>

              <Card hoverable>
                <CardHeader title="Return Reasons" subtitle={`${summary.returns_count} total`} icon={<Undo2 className="w-4 h-4" />} />
                <CardBody className="space-y-1">
                  {summary.return_reasons.length === 0 ? (
                    <EmptyState title="No returns in this range" />
                  ) : (
                    summary.return_reasons.map((r) => (
                      <div key={r.reason} className="flex items-center justify-between px-2 py-2">
                        <p className="text-[12.5px] text-ink">{r.reason}</p>
                        <p className="text-[13px] font-bold text-ink tabular-nums">{r.count}</p>
                      </div>
                    ))
                  )}
                </CardBody>
              </Card>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
